import React from 'react'
import clsx from 'clsx'
import { useModal } from '@hooks/useModal'
import { useSiteContext } from '@contexts/MkSiteContext'
import { configs } from '@configs'
import { BASENAME, PUBLIC_PATH } from '@utils/basePath'
import { getAemExperienceFragment, getCachedExperienceFragment, transformBrand } from '../../../utils/aemApi'
import './ExperienceFragment.scss'

export interface ExperienceFragmentProps {
  brand: string
  name: string
  className?: string
  processLogo?: boolean
  scrolled?: boolean
}

function makeRelativeUrls(html: string, skipHrefProcessing: boolean = false): string {
  let processedHtml = html

  // Only process href attributes if not skipping (e.g., for footer links)
  if (!skipHrefProcessing) {
    processedHtml = processedHtml
      .replace(/href=["']https?:\/\/([^.]+\.)?tnlmako\.com\//g, `href="${PUBLIC_PATH}`)
      .replace(/href=["']https?:\/\/([^.]+\.)?sportsillustratedresorts\.com\//g, `href="${PUBLIC_PATH}`)

    // Convert /us/en prefixed URLs to absolute URLs using AEM base URL
    if (configs.mk.aemXfBaseUrl) {
      processedHtml = processedHtml.replace(/href=["']\/us\/en/g, `href="${configs.mk.aemXfBaseUrl}/us/en"`)
    }
  }

  // Prefix root-relative asset sources with the app's PUBLIC_PATH unless already base-prefixed
  try {
    const baseNoSlash = BASENAME.replace(/^\//, '') // e.g., 'mk'
    if (baseNoSlash) {
      const escaped = baseNoSlash.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      // Matches src="/..." or data-src="/..." where the path is NOT already /mk/...
      const re = new RegExp(`(\\s(?:src|data-src)=["'])/(?!${escaped}/)`, 'gi')
      processedHtml = processedHtml.replace(re, `$1${PUBLIC_PATH}`)
    }
  } catch (e) {
    // noop – on regex failure, leave HTML as-is
  }

  return processedHtml
}

// Helper functions for link detection
const isRegisterLink = (href: string | null, linkText: string): boolean => {
  const registerPatterns = [/\/register/, /register/]
  const textPatterns = ['register']

  return registerPatterns.some((pattern) => pattern.test(href || '')) || textPatterns.some((pattern) => linkText.includes(pattern))
}

const isLoginLink = (href: string | null, linkText: string): boolean => {
  const loginPatterns = [/\/login/, /login/]
  const textPatterns = ['sign in', 'login']

  return loginPatterns.some((pattern) => pattern.test(href || '')) || textPatterns.some((pattern) => linkText.includes(pattern))
}

export const ExperienceFragment: React.FC<ExperienceFragmentProps> = ({ brand, name, className, processLogo = false, scrolled = false }) => {
  const transformedBrand = transformBrand(brand)
  const { openSignInModal, openRegisterModal } = useModal({ modalID: 'aem-header-modal' })
  const { headerLogo, headerLogoDark } = useSiteContext()

  const [content, setContent] = React.useState<string>('')
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const fragmentRef = React.useRef<HTMLDivElement>(null)
  const processedLinksRef = React.useRef<Map<Element, { type: 'register' | 'login'; listener: EventListener }>>(new Map())

  // Memoize the selector for better performance
  const authLinkSelector = React.useMemo(() => 'a[href*="/register"], a[href*="/login"], a[href*="register"], a[href*="login"]', [])

  // Function to update logo when scroll state changes
  const updateLogoOnScroll = React.useCallback(() => {
    if (!processLogo) return

    const fragmentElement = fragmentRef.current
    if (!fragmentElement || !headerLogo?.src || !headerLogoDark?.src) return

    const logoImg = fragmentElement.querySelector('.logo img[data-logo-src]') as HTMLImageElement
    if (logoImg) {
      logoImg.src = scrolled ? headerLogo.src : headerLogoDark.src
    }
  }, [processLogo, scrolled, headerLogo, headerLogoDark])

  // Function to process logo in the HTML content
  const processLogoInHtml = React.useCallback(
    (html: string): string => {
      if (!processLogo || !headerLogo?.src || !headerLogoDark?.src) return html

      // Find the logo div and replace it with our logo logic
      const logoDivRegex = /<div class="logo"[^>]*>[\s\S]*?<\/div>/g
      const logoReplacement = `
        <div class="logo">
          <a class="header__logo-link" id="navLogo" data-testid="mk-nav-logo" href="${PUBLIC_PATH}">
            <img
              src="${scrolled ? headerLogo.src : headerLogoDark.src}"
              alt="${headerLogo.alt || 'Logo'}"
              class="header__logo-image"
              data-logo-src="${headerLogo.src}"
              data-logo-dark-src="${headerLogoDark.src}"
            />
          </a>
        </div>
      `

      return html.replace(logoDivRegex, logoReplacement)
    },
    [processLogo, headerLogo, headerLogoDark, scrolled],
  )

  // Update logo when scroll state changes
  React.useEffect(() => {
    updateLogoOnScroll()
  }, [updateLogoOnScroll])

  // Function to intercept register/login links
  const interceptAuthLinks = React.useCallback(() => {
    const fragmentElement = fragmentRef.current
    if (!fragmentElement) return

    // Find all links that might be register/login links using memoized selector
    const links = fragmentElement.querySelectorAll(authLinkSelector)

    links.forEach((link) => {
      // Skip if we've already processed this link
      if (processedLinksRef.current.has(link)) return

      const href = link.getAttribute('href')
      const linkText = link.textContent?.toLowerCase().trim() || ''

      // Check if this is a register link
      if (isRegisterLink(href, linkText)) {
        const listener = (e: Event) => {
          e.preventDefault()
          e.stopPropagation()
          openRegisterModal('aem-register-modal')
        }

        link.addEventListener('click', listener)
        ;(link as HTMLElement).style.cursor = 'pointer'
        processedLinksRef.current.set(link, { type: 'register', listener })
      }

      // Check if this is a login/signin link
      if (isLoginLink(href, linkText)) {
        const listener = (e: Event) => {
          e.preventDefault()
          e.stopPropagation()
          openSignInModal('aem-signin-modal')
        }

        link.addEventListener('click', listener)
        ;(link as HTMLElement).style.cursor = 'pointer'
        processedLinksRef.current.set(link, { type: 'login', listener })
      }
    })
  }, [openSignInModal, openRegisterModal, authLinkSelector])

  // Cleanup function to remove event listeners
  const cleanupEventListeners = React.useCallback(() => {
    processedLinksRef.current.forEach(({ listener }, link) => {
      link.removeEventListener('click', listener)
    })
    processedLinksRef.current.clear()
  }, [])

  React.useEffect(() => {
    // Check cache synchronously for immediate rendering
    const cachedContent = getCachedExperienceFragment(transformedBrand, name)

    // Skip href processing for footer to avoid adding /mk prefix to links
    const skipHrefProcessing = name === 'footer'

    if (cachedContent) {
      const processedContent = processLogoInHtml(makeRelativeUrls(cachedContent, skipHrefProcessing))
      setContent(processedContent)
      setIsLoading(false)
      return undefined
    }

    let isMounted = true
    const fetchFragment = async () => {
      try {
        const html = await getAemExperienceFragment(transformedBrand, name)
        const processedHtml = processLogoInHtml(makeRelativeUrls(html, skipHrefProcessing))
        if (isMounted) {
          setContent(processedHtml)
          setIsLoading(false)
        }
      } catch (err: any) {
        console.error('Failed to fetch experience fragment:', err)
        if (isMounted) {
          setContent('') // Set empty content on error
          setIsLoading(false)
        }
      }
    }
    fetchFragment()

    return () => {
      isMounted = false
    }
  }, [transformedBrand, name, processLogoInHtml])

  // Intercept links after content is loaded
  React.useEffect(() => {
    if (!isLoading && content && name === 'header') {
      // Clean up previous event listeners before adding new ones
      cleanupEventListeners()

      // Use a small delay to ensure the DOM is fully rendered
      const timer = setTimeout(() => {
        interceptAuthLinks()
      }, 100)

      return () => {
        clearTimeout(timer)
        cleanupEventListeners()
      }
    }
  }, [content, isLoading, name, interceptAuthLinks, cleanupEventListeners])

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      cleanupEventListeners()
    }
  }, [cleanupEventListeners])

  if (isLoading) {
    return <div className={clsx('aem-xf', className)}>Loading...</div>
  }

  // If no content, render empty div so parent can detect it
  if (!content || content.trim() === '') {
    return (
      <div
        className={clsx('aem-xf', className)}
        data-experience-fragment
      />
    )
  }

  return (
    <div
      ref={fragmentRef}
      className={clsx('aem-xf', className)}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: content }}
      data-experience-fragment
    />
  )
}
